<!--
  Storico Ordini — lista degli ordini chiusi.
  Redesigned 2026-06-15 (v2.9.0, Phase 1.5 Chunk H.1).

  Drops kendo-grid + 2× kendo-datepicker + 5× kendo-grid-column +
  kendo-grid-command-column + kendoGridEditCommand + kendoGridCellTemplate
  (× 5) + kendoGridNoRecordsTemplate + ~10 inline-style
  display:flex!important + position:fixed clear icon z-index 10001
  (11 kendo refs → 0).

  Pattern Diakonix table-card coerente con storico-parziali G.1:
    - Sticky header con titolo + chip viola "Parziali" + counter
    - Filter bar nativa (search + clear + date range + customer select)
    - Diakonix table 6 colonne con padding 14×16
    - Row `--partial` tint viola se `or_partial === 'S'`
    - Per-row action: ℹ️ Dettagli

  Sub-modale preserved:
    - app-storico-ordini-dettagli (modale dettagli, Chunk H.2)
-->

<div class="storico-page">

  <!-- ─── Sticky header ────────────────────────────────────────── -->
  <header class="storico-header">
    <div class="storico-header__titles">
      <h1 class="storico-header__title">
        📚 Storico ordini
         <span class="status-chip status-chip--partial" title="Tutti gli ordini in questa lista hanno consegna parziale">
          Parziale
        </span>
      </h1>
      <p class="storico-header__subtitle">
        @if (displayData.length > 0) {
          <strong>{{ displayData.length }}</strong>
          {{ displayData.length === 1 ? 'ordine chiuso' : 'ordini chiusi' }}
          @if (filterTerm) { · ricerca <strong>"{{ filterTerm }}"</strong> }
          @if (selectedCustomer) { · cliente <strong>{{ selectedCustomer }}</strong> }
        } @else if (loading) {
          Caricamento storico…
        } @else {
          Nessun ordine chiuso al momento
        }
      </p>
    </div>

    <button
      type="button"
      class="refresh-btn"
      (click)="getOrders()"
      title="Aggiorna lista"
      aria-label="Aggiorna lista"
    >🔁</button>
  </header>

  <!-- ─── Filter bar ───────────────────────────────────────────── -->
  <section class="storico-filters" aria-label="Filtri ricerca">

    <!-- Search -->
    <div class="filter-field">
      <label class="filter-field__label" for="searchStorico">Cerca ordine</label>
      <div class="filter-field__input-wrap">
        <input
          id="searchStorico"
          type="search"
          class="filter-field__input"
          placeholder="N° ordine o cliente…"
          [(ngModel)]="filterTerm"
          (keyup.enter)="searchOrders()"
        />
        @if (filterTerm) {
          <button
            type="button"
            class="filter-field__clear"
            (click)="clearSearch()"
            aria-label="Rimuovi filtro"
          >×</button>
        }
      </div>
    </div>

    <!-- Date range -->
    <div class="filter-field filter-field--dates">
      <label class="filter-field__label">Data ordine</label>
      <div class="filter-field__date-pair">
        <input
          type="date"
          class="filter-field__input filter-field__input--date"
          [ngModel]="filters.startingDate | date:'yyyy-MM-dd'"
          (ngModelChange)="onStartDateChange($event)"
          aria-label="Data da"
        />
        <span class="filter-field__date-sep">→</span>
        <input
          type="date"
          class="filter-field__input filter-field__input--date"
          [ngModel]="filters.endDate | date:'yyyy-MM-dd'"
          (ngModelChange)="onEndDateChange($event)"
          aria-label="Data a"
        />
      </div>
    </div>

    <!-- Customer select -->
    <div class="filter-field">
      <label class="filter-field__label" for="filterCustomer">Cliente</label>
      <select
        id="filterCustomer"
        class="filter-field__input filter-field__input--select"
        [(ngModel)]="selectedCustomer"
        (ngModelChange)="onCustomerChange($event)"
      >
        @for (customer of customers; track customer) {
          <option [ngValue]="customer === showAll ? null : customer">
            {{ customer || '— non specificato —' }}
          </option>
        }
      </select>
    </div>

    <button
      type="button"
      class="btn btn--primary"
      (click)="searchOrders()"
      [disabled]="loading"
    >🔍 Cerca</button>
  </section>

  <!-- ─── Table card ───────────────────────────────────────────── -->
  <section class="storico-table-card" aria-label="Elenco ordini chiusi">

    @if (loading) {
      <div class="table-skeleton" aria-busy="true">
        @for (i of [1, 2, 3, 4, 5, 6, 7, 8]; track i) {
          <div class="table-skeleton__row">
            <div class="table-skeleton__line table-skeleton__line--lg"></div>
            <div class="table-skeleton__line table-skeleton__line--md"></div>
            <div class="table-skeleton__line table-skeleton__line--sm"></div>
          </div>
        }
      </div>
    } @else if (displayData.length === 0) {
      <div class="storico-empty">
        <div class="storico-empty__icon" aria-hidden="true">
          @if (filterTerm || selectedCustomer) { 🔎 } @else { 📭 }
        </div>
        <p class="storico-empty__msg">
          @if (filterTerm || selectedCustomer) {
            Nessun ordine corrisponde ai filtri attivi.
          } @else {
            Nessun ordine chiuso registrato al momento.
          }
        </p>
      </div>
    } @else {
      <div class="storico-table-scroll">
        <table class="storico-table" role="table">
          <thead>
            <tr>
              <th class="col-order sortable" (click)="sort('or_order_number')">
                N° Ordine <span class="sort-icon">{{ sortIcon('or_order_number') }}</span>
              </th>
              <th class="col-customer sortable" (click)="sort('or_customer')">
                Cliente <span class="sort-icon">{{ sortIcon('or_customer') }}</span>
              </th>
              <th class="col-operator sortable" (click)="sort('ac_last_name')">
                Operatore <span class="sort-icon">{{ sortIcon('ac_last_name') }}</span>
              </th>
              <th class="col-date sortable" (click)="sort('or_order_date')">
                Data ordine <span class="sort-icon">{{ sortIcon('or_order_date') }}</span>
              </th>
              <th class="col-date sortable" (click)="sort('or_closed_date')">
                Data evasione <span class="sort-icon">{{ sortIcon('or_closed_date') }}</span>
              </th>
              <th class="col-actions">Azioni</th>
            </tr>
          </thead>
          <tbody>
            @for (
              item of displayData | sortBy : sortField : sortDir;
              track item.order_id + '-' + ($index)
            ) {
              <tr
                class="storico-row"
                [class.storico-row--partial]="isPartial(item)"
                (click)="goToDetails(item.order_id)"
                [title]="isPartial(item) ? 'Ordine parziale — apri dettagli' : 'Apri dettagli ordine'"
              >
                <td class="col-order">
                  <span class="cell-order-num">{{ item.or_order_number || '—' }}</span>
                  @if (isPartial(item)) {
                    <span class="cell-partial-tag" aria-label="Ordine parziale">P</span>
                  }
                </td>
                <td class="col-customer">
                  <span class="cell-customer">{{ item.or_customer || '—' }}</span>
                </td>
                <td class="col-operator">
                  <span class="cell-operator">
                    @if (item.ac_first_name || item.ac_last_name) {
                      {{ item.ac_first_name }} {{ item.ac_last_name }}
                    } @else { — }
                  </span>
                </td>
                <td class="col-date">
                  <span class="cell-date">
                    {{ item.or_order_date ? (item.or_order_date | date:'dd/MM/yyyy') : '—' }}
                  </span>
                </td>
                <td class="col-date">
                  <span class="cell-date">
                    {{ item.or_closed_date ? (item.or_closed_date | date:'dd/MM/yyyy') : '—' }}
                  </span>
                </td>
                <td class="col-actions" (click)="$event.stopPropagation()">
                  <div class="row-actions">
                    <button
                      type="button"
                      class="icon-btn icon-btn--primary"
                      title="Apri dettagli ordine"
                      aria-label="Apri dettagli ordine"
                      (click)="goToDetails(item.order_id)"
                    >ℹ️</button>
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }
  </section>

</div>

<!-- ─── Sub-modale dettagli (Chunk H.2 — restyle in arrivo) ───── -->
@if (closeSrv.isStorico) {
  <app-storico-ordini-dettagli
    [opened]="closeSrv.isStorico"
    [order_id]="clickedOrder"
  ></app-storico-ordini-dettagli>
}
